import React from 'react';

import { Skeleton } from 'core/cells/skeleton';
import { createReactTableStore } from 'core/memory/pure/react-table-store';
import { useGetTermsState } from 'core/memory/apollo/terms/local/get-terms-state';
import {
  useReactTableExport,
  useReactTableState,
} from 'core/metabolism/table-state';
import { Absence } from 'modules/student/dna/types/absence';
import { createStyles, makeStyles } from 'core/atoms/styles';
import { useTranslations } from 'core/dna/translations';

import { Card, CardBody } from 'templates/mdp/components/card';
import { Button } from 'templates/mdp/components/custom-buttons';

import { Table } from 'templates/mdp/components/react-table';
import {
  AbsenceTableItem,
  useAbsenceTableRead,
} from './use-absence-table-read';

const useStyles = makeStyles(() =>
  createStyles({
    exportBtn: {
      position: 'absolute',
      right: 0,
      bottom: 'calc(100% + 8px)',
    },
  }),
);

interface AbsenceTableProps {
  data: Absence[] | undefined;
  loading: boolean;
}

export const absenceTableStore = createReactTableStore({}, {});

export const AbsenceTable = (props: AbsenceTableProps) => {
  const { loading, data } = props;
  const classes = useStyles();

  const { texts } = useTranslations();

  const { columns, tableData } = useAbsenceTableRead(data);
  const { data: termsState } = useGetTermsState();

  const { exportToExcel } = useReactTableExport<AbsenceTableItem>(
    columns,
    tableData,
  );

  const {
    initialPage,
    pageSize,
    sort,
    filters,
    setSort,
    setFilters,
    setPage,
    setPageSize,
  } = useReactTableState(absenceTableStore, termsState?.selected ?? undefined);

  return loading || !termsState?.selected || !tableData ? (
    <Skeleton variant="rect" width="100%" height="300px" />
  ) : (
    <Card>
      {!!data?.length && (
        <Button
          className={classes.exportBtn}
          color="secondary"
          size="sm"
          onClick={() => {
            exportToExcel(`${data[0].regarding} Fravær`);
          }}
        >
          {texts.oasCommon.exportTo} excel
        </Button>
      )}
      <CardBody>
        <Table
          noActions
          noPagination
          columns={columns}
          data={tableData}
          key={termsState?.selected ?? 'unknown_term'}
          onChangeRowsPerPage={setPageSize}
          onChangePage={setPage}
          onFilterChange={setFilters}
          onOrderChange={setSort}
          options={{
            initialPage,
            pageSize,
            sort,
            filters,
          }}
        />
      </CardBody>
    </Card>
  );
};
